import axios from "axios";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  useNavigate,
  useParams,
} from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "Guias";

const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

// =====================================================
// OBTENER MENSAJE DE ERROR CUANDO LA RESPUESTA ES BLOB
// =====================================================
const getBlobErrorMessage = async (
  error,
  fallback = "No se pudo generar la guía."
) => {
  try {
    const data = error?.response?.data;

    if (data instanceof Blob) {
      const text = await data.text();

      try {
        const json = JSON.parse(text);

        return (
          json?.message ||
          json?.error ||
          fallback
        );
      } catch {
        return text || fallback;
      }
    }

    return (
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      fallback
    );
  } catch {
    return fallback;
  }
};

// =====================================================
// OBTENER EL NOMBRE DEL ARCHIVO DESDE LOS HEADERS
// =====================================================
const getFileNameFromDisposition = (
  contentDisposition,
  fallbackName
) => {
  if (!contentDisposition) {
    return fallbackName;
  }

  const utf8Match =
    contentDisposition.match(
      /filename\*=UTF-8''([^;]+)/i
    );

  if (utf8Match?.[1]) {
    return decodeURIComponent(
      utf8Match[1]
    );
  }

  const normalMatch =
    contentDisposition.match(
      /filename="?([^"]+)"?/i
    );

  if (normalMatch?.[1]) {
    return normalMatch[1];
  }

  return fallbackName;
};

const ListarGuiasdeAprendizaje = ({
  refresh,
}) => {
  const [guias, setGuias] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [
    deletingId,
    setDeletingId,
  ] = useState(null);

  const [
    downloadingId,
    setDownloadingId,
  ] = useState(null);

  const { codPrograma } =
    useParams();

  const navigate = useNavigate();

  // =====================================================
  // CONFIGURACIÓN DE AUTENTICACIÓN
  // =====================================================
  const authConfig = useCallback(() => {
    const token =
      localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }, []);

  // =====================================================
  // OBTENER PERMISOS DEL MÓDULO GUÍAS
  // =====================================================
  const getModuloGuias = () => {
    try {
      const permisosStorage =
        localStorage.getItem(
          "permisos"
        );

      const usuarioStorage =
        localStorage.getItem(
          "usuario"
        );

      const userStorage =
        localStorage.getItem(
          "user"
        );

      const usuariosStorage =
        localStorage.getItem(
          "usuarios"
        );

      const permisosData =
        permisosStorage
          ? JSON.parse(
              permisosStorage
            )
          : usuarioStorage
          ? JSON.parse(
              usuarioStorage
            )
          : userStorage
          ? JSON.parse(
              userStorage
            )
          : usuariosStorage
          ? JSON.parse(
              usuariosStorage
            )
          : {};

      const modulos = Array.isArray(
        permisosData
      )
        ? permisosData
        : permisosData?.modulos ||
          permisosData?.permisos ||
          permisosData?.usuario
            ?.modulos ||
          permisosData?.usuario
            ?.permisos ||
          permisosData?.user
            ?.modulos ||
          permisosData?.user
            ?.permisos ||
          [];

      const moduloBuscado =
        normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo ||
            modulo?.modulo ||
            modulo?.nombre ||
            "";

          return (
            normalizarTexto(nombre) ===
            moduloBuscado
          );
        }) || {}
      );
    } catch (error) {
      console.error(
        "Error leyendo permisos:",
        error
      );

      return {};
    }
  };

  const moduloGuias = useMemo(
    () => getModuloGuias(),
    []
  );

  const puedeCrear =
    moduloGuias?.puedeCrear ===
      true ||
    moduloGuias?.crear === true ||
    moduloGuias?.create === true;

  const puedeActualizar =
    moduloGuias?.puedeActualizar ===
      true ||
    moduloGuias?.actualizar ===
      true ||
    moduloGuias?.editar === true ||
    moduloGuias?.update === true;

  const puedeEliminar =
    moduloGuias?.puedeEliminar ===
      true ||
    moduloGuias?.eliminar ===
      true ||
    moduloGuias?.delete === true;

  // =====================================================
  // CONTROLAR SESIÓN EXPIRADA
  // =====================================================
  const manejarErrorSesion =
    useCallback(
      (error) => {
        if (
          error?.response?.status ===
          401
        ) {
          Swal.fire({
            icon: "warning",
            title:
              "Sesión expirada",
            text: "Por favor inicia sesión nuevamente.",
            confirmButtonText:
              "Aceptar",
          }).then(() => {
            localStorage.removeItem(
              "token"
            );

            navigate("/login", {
              replace: true,
            });
          });

          return true;
        }

        return false;
      },
      [navigate]
    );

  // =====================================================
  // GENERAR Y DESCARGAR GUÍA
  // =====================================================
  const generarYDescargarGuia =
    useCallback(
      async (
        idGuia,
        codProgramaValue,
        numeroGuia
      ) => {
        if (!idGuia) {
          Swal.fire(
            "Atención",
            "No se recibió el ID de la guía.",
            "warning"
          );

          return;
        }

        if (downloadingId) {
          return;
        }

        try {
          setDownloadingId(idGuia);

          const response =
            await axios.get(
              `/api/guia-aprendizaje/generar-docx/${idGuia}`,
              {
                ...authConfig(),
                responseType:
                  "blob",
              }
            );

          const contentType =
            response.headers?.[
              "content-type"
            ] || "";

          if (
            contentType.includes(
              "application/json"
            )
          ) {
            const text =
              await response.data.text();

            try {
              const json =
                JSON.parse(text);

              throw new Error(
                json?.message ||
                  "El servidor devolvió un error."
              );
            } catch {
              throw new Error(
                "El servidor no devolvió un documento válido."
              );
            }
          }

          const fallbackName =
            `GuiaAprendizaje_${
              codProgramaValue ||
              "Programa"
            }_N${
              numeroGuia || ""
            }.docx`;

          const fileName =
            getFileNameFromDisposition(
              response.headers?.[
                "content-disposition"
              ],
              fallbackName
            );

          const blob = new Blob(
            [response.data],
            {
              type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            }
          );

          const url =
            window.URL.createObjectURL(
              blob
            );

          const link =
            document.createElement("a");

          link.href = url;
          link.download = fileName;

          document.body.appendChild(
            link
          );

          link.click();
          link.remove();

          window.URL.revokeObjectURL(
            url
          );
        } catch (error) {
          console.error(
            "Error al generar la guía:",
            error
          );

          if (
            manejarErrorSesion(error)
          ) {
            return;
          }

          const msg =
            await getBlobErrorMessage(
              error,
              error?.message ||
                "No se pudo generar la guía."
            );

          Swal.fire(
            "Error",
            msg,
            "error"
          );
        } finally {
          setDownloadingId(null);
        }
      },
      [
        authConfig,
        downloadingId,
        manejarErrorSesion,
      ]
    );

  // =====================================================
  // ELIMINAR GUÍA
  // =====================================================
  const deleteGuia = useCallback(
    async (idGuia) => {
      if (!puedeEliminar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para eliminar guías.",
          "warning"
        );

        return;
      }

      try {
        const confirm =
          await Swal.fire({
            title:
              "¿Está seguro?",
            text: "¡Esta acción no se puede deshacer!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor:
              "#3085d6",
            cancelButtonColor:
              "#d33",
            confirmButtonText:
              "Sí, eliminar",
            cancelButtonText:
              "Cancelar",
          });

        if (!confirm.isConfirmed) {
          return;
        }

        setDeletingId(idGuia);

        const { data } =
          await axios.delete(
            `/api/programas/guias/delete/${idGuia}`,
            authConfig()
          );

        setGuias((prev) =>
          prev.filter(
            (guia) =>
              guia._id !== idGuia
          )
        );

        Swal.fire({
          icon: "success",
          title:
            data.message ||
            "Guía eliminada",
          showConfirmButton: false,
          timer: 1500,
        });
      } catch (error) {
        console.error(
          "Error al eliminar la guía:",
          error
        );

        if (
          manejarErrorSesion(error)
        ) {
          return;
        }

        Swal.fire(
          "Error",
          "No se pudo eliminar la guía.",
          "error"
        );
      } finally {
        setDeletingId(null);
      }
    },
    [
      authConfig,
      puedeEliminar,
      manejarErrorSesion,
    ]
  );

  // =====================================================
  // CARGAR GUÍAS DEL PROGRAMA
  // =====================================================
  const getData = useCallback(
    async () => {
      if (!codPrograma) {
        return;
      }

      try {
        setLoading(true);

        const response =
          await axios.get(
            `/api/programas/guias/listidprograma/${codPrograma}`,
            authConfig()
          );

        if (
          response.data &&
          response.data.ok &&
          Array.isArray(
            response.data.guias
          )
        ) {
          const guiasOrdenadas = [
            ...response.data.guias,
          ].sort(
            (a, b) =>
              new Date(
                b.createdAt
              ) -
              new Date(
                a.createdAt
              )
          );

          const total =
            guiasOrdenadas.length;

          const guiasConNumero =
            guiasOrdenadas.map(
              (guia, index) => ({
                ...guia,

                numeroGuia:
                  total - index,
              })
            );

          setGuias(
            guiasConNumero
          );
        } else {
          setGuias([]);
        }
      } catch (error) {
        console.error(
          "Error al cargar las guías:",
          error
        );

        if (
          manejarErrorSesion(error)
        ) {
          return;
        }

        Swal.fire(
          "Error",
          "No se pudieron cargar las guías.",
          "error"
        );

        setGuias([]);
      } finally {
        setLoading(false);
      }
    },
    [
      codPrograma,
      authConfig,
      manejarErrorSesion,
    ]
  );

  useEffect(() => {
    getData();
  }, [getData, refresh]);

  // =====================================================
  // PREPARAR DATOS PARA LA TABLA
  // =====================================================
  const guiasTabla = useMemo(() => {
    return guias.map((guia) => ({
      ...guia,

      funciones: (
        <div className="d-flex align-items-center justify-content-center gap-2 flex-nowrap">

          {/* DESCARGAR */}
          <button
            className="btn btn-dark btn-sm d-inline-flex align-items-center justify-content-center"
            style={{
              width: 34,
              height: 34,
            }}
            onClick={() =>
              generarYDescargarGuia(
                guia._id,
                guia.codPrograma,
                guia.numeroGuia
              )
            }
            title={`Descargar Guía N° ${guia.numeroGuia}`}
            type="button"
            disabled={
              downloadingId ===
                guia._id ||
              Boolean(deletingId)
            }
          >
            <i
              className={`fa-solid ${
                downloadingId ===
                guia._id
                  ? "fa-spinner fa-spin"
                  : "fa-file-word"
              }`}
            ></i>
          </button>

          {/* EDITAR */}
          {puedeActualizar && (
            <button
              className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
              style={{
                width: 34,
                height: 34,
              }}
              onClick={() =>
                navigate(
                  `/institucional/guias/editar/${guia._id}`
                )
              }
              title="Editar guía"
              type="button"
              disabled={
                Boolean(
                  downloadingId
                ) ||
                deletingId ===
                  guia._id
              }
            >
              <i className="fa-solid fa-pen-to-square"></i>
            </button>
          )}

          {/* ELIMINAR */}
          {puedeEliminar && (
            <button
              className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
              style={{
                width: 34,
                height: 34,
              }}
              onClick={() =>
                deleteGuia(
                  guia._id
                )
              }
              title="Eliminar guía"
              type="button"
              disabled={
                deletingId ===
                  guia._id ||
                Boolean(
                  downloadingId
                )
              }
            >
              <i
                className={`fa-solid ${
                  deletingId ===
                  guia._id
                    ? "fa-spinner fa-spin"
                    : "fa-trash"
                }`}
              ></i>
            </button>
          )}
        </div>
      ),
    }));
  }, [
    guias,
    downloadingId,
    deletingId,
    puedeActualizar,
    puedeEliminar,
    navigate,
    generarYDescargarGuia,
    deleteGuia,
  ]);

  // =====================================================
  // COLUMNAS DE LA TABLA
  // =====================================================
  const columns = useMemo(
    () => [
      {
        Header: "N° Guía",
        accessor:
          "numeroGuia",
      },
      {
        Header: "Programa",
        accessor:
          "nombrePrograma",
      },
      {
        Header:
          "Proyecto Formativo",
        accessor:
          "nombreProyectoFormativo",
      },
      {
        Header: "Fase",
        accessor:
          "faseProyecto",
      },
      {
        Header: "Acciones",
        accessor:
          "funciones",
      },
    ],
    []
  );

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">

          {/* =================================================
              ENCABEZADO
          ================================================= */}

          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">

              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Guías de aprendizaje
                </h5>

                <small className="text-secondary">
                  Gestión de guías por
                  programa: edición,
                  descarga y eliminación.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">

                {/* VOLVER A LA PÁGINA ANTERIOR */}
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() =>
                    navigate(-1)
                  }
                  title="Volver a la página anterior"
                  disabled={
                    loading ||
                    Boolean(
                      downloadingId
                    )
                  }
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>

                {/* ACTUALIZAR */}
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={getData}
                  title="Actualizar"
                  disabled={
                    loading ||
                    Boolean(
                      downloadingId
                    )
                  }
                >
                  <i
                    className={`fa-solid ${
                      loading
                        ? "fa-spinner fa-spin"
                        : "fa-rotate"
                    } me-2`}
                  ></i>

                  Actualizar
                </button>

                {/* NUEVA GUÍA */}
                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={() =>
                      navigate(
                        `/institucional/guias/nuevo/${codPrograma}`
                      )
                    }
                    disabled={
                      loading ||
                      !codPrograma ||
                      Boolean(
                        downloadingId
                      )
                    }
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Nuevo
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* =================================================
              CONTENIDO
          ================================================= */}

          <div>
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div
                  className="spinner-border"
                  role="status"
                  aria-hidden="true"
                ></div>

                <div className="mt-3">
                  Cargando guías…
                </div>
              </div>
            ) : Array.isArray(
                guiasTabla
              ) &&
              guiasTabla.length >
                0 ? (
              <div className="overflow-hidden">
                <DataTable
                  columns={columns}
                  data={guiasTabla}
                />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">

                <div className="display-6 mb-2">
                  📄
                </div>

                <div className="fw-semibold">
                  No hay guías disponibles
                </div>

                <div className="small">
                  No se encontraron guías
                  para este programa.
                </div>

                {puedeCrear && (
                  <button
                    className="btn btn-dark mt-3"
                    type="button"
                    onClick={() =>
                      navigate(
                        `/institucional/guias/nuevo/${codPrograma}`
                      )
                    }
                    disabled={
                      Boolean(
                        downloadingId
                      ) ||
                      !codPrograma
                    }
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Registrar guía
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarGuiasdeAprendizaje;